// src/components/Admin/InstitutionCmp/InstitutionProfile/InstitutionSubscription.js
//
// F5 - Card de autogestion de la suscripcion Stripe. SOLO se renderiza para
// instituciones compradas por la web (hasStripeSubscription). Cancelar usa
// cancel_at_period_end: se conserva el acceso hasta el final del periodo ya
// pagado (mes o anio) y no hay mas cobros; reactivable hasta esa fecha.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Segment,
  Header,
  Button,
  Modal,
  Message,
  Label,
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAuth } from "../../../../hooks";
import { BillingApi } from "../../../../api";

export function InstitutionSubscription() {
  const { accessToken } = useAuth();
  const { t, i18n } = useTranslation("institutions");
  const billingApi = useMemo(() => new BillingApi(), []);

  const [sub, setSub] = useState(null);
  const [working, setWorking] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetchSub = useCallback(async () => {
    try {
      const res = await billingApi.getMySubscription(accessToken);
      setSub(res?.data || null);
    } catch (e) {
      // Silencioso: sin datos no se pinta la card.
      setSub(null);
    }
  }, [accessToken, billingApi]);

  useEffect(() => {
    if (accessToken) fetchSub();
  }, [accessToken, fetchSub]);

  if (!sub?.hasStripeSubscription) return null;

  const endDate = sub.current_period_end
    ? new Date(sub.current_period_end).toLocaleDateString(i18n.language, {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "–";
  const intervalLabel =
    sub.interval === "year"
      ? t("subscription.intervalAnnual")
      : t("subscription.intervalMonthly");

  const handleCancel = async () => {
    setWorking(true);
    try {
      const res = await billingApi.cancelSubscription(accessToken);
      setSub(res?.data || null);
      setConfirmOpen(false);
      toast.success(t("subscription.cancelSuccess"));
    } catch (e) {
      toast.error(e.message || t("subscription.error"));
    } finally {
      setWorking(false);
    }
  };

  const handleReactivate = async () => {
    setWorking(true);
    try {
      const res = await billingApi.reactivateSubscription(accessToken);
      setSub(res?.data || null);
      toast.success(t("subscription.reactivateSuccess"));
    } catch (e) {
      toast.error(e.message || t("subscription.error"));
    } finally {
      setWorking(false);
    }
  };

  return (
    <Segment>
      <Header as="h3">
        {t("subscription.title")}{" "}
        <Label
          size="small"
          color={sub.cancel_at_period_end ? "orange" : "green"}
        >
          {sub.cancel_at_period_end
            ? t("subscription.statusEnding")
            : t("subscription.statusActive")}
        </Label>
      </Header>

      {sub.cancel_at_period_end ? (
        <>
          <Message warning>
            {t("subscription.endsOnNotice", { date: endDate })}
          </Message>
          <Button
            primary
            onClick={handleReactivate}
            loading={working}
            disabled={working}
          >
            {t("subscription.reactivateButton")}
          </Button>
        </>
      ) : (
        <>
          <p>
            {t("subscription.renewsOn", {
              interval: intervalLabel,
              date: endDate,
            })}
          </p>
          <Button
            basic
            negative
            size="small"
            onClick={() => setConfirmOpen(true)}
            disabled={working}
          >
            {t("subscription.cancelButton")}
          </Button>
          <Modal
            size="tiny"
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
          >
            <Modal.Header>{t("subscription.modalTitle")}</Modal.Header>
            <Modal.Content>
              <p>{t("subscription.modalBody", { date: endDate })}</p>
            </Modal.Content>
            <Modal.Actions>
              <Button onClick={() => setConfirmOpen(false)} disabled={working}>
                {t("subscription.modalKeep")}
              </Button>
              <Button
                negative
                onClick={handleCancel}
                loading={working}
                disabled={working}
              >
                {t("subscription.modalConfirm")}
              </Button>
            </Modal.Actions>
          </Modal>
        </>
      )}
    </Segment>
  );
}
